import React from 'react';
import {
    RibbonTopBar,
    type RibbonTabDefinitionBase,
    type RibbonTabDefinitionMLR,
    type RibbonTabDefinitionSLR,
    type RibbonTopBarProps,
    type TabHeader,
} from 'owa-acui';
import { appIcons, getAppStrings, getAppColors } from 'owa-acui-theme';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import { observer } from 'owa-mobx-react';
import classnames from 'owa-classnames';
import { useFocusFinders } from '@fluentui/react-components';
import { topBar, topBarContainer, topBarWin32Overlay } from './CommandRibbonTopBar.scss';
import {
    RIBBON_TAB_FONT_SIZE,
    RIBBON_TAB_FONT_WEIGHT,
    RIBBON_TAB_HORIZONTAL_PADDING,
    RIBBON_TAB_LINE_HEIGHT,
    RIBBON_TAB_MIN_HEIGHT,
} from '../util/ribbonTabStyleConstants';

export interface CommandRibbonTopBarProps {
    extraControlsAfterTabs?: React.ReactNode;
    extraControlsBeforeTabs?: React.ReactNode;
    onTabHeaderClick: (tabId: string) => void;
    ribbonTabs: RibbonTabDefinitionBase[];
    selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR;
}

export const CommandRibbonTopBar: React.FunctionComponent<CommandRibbonTopBarProps> = observer(
    function CommandRibbonTopBar({
        extraControlsAfterTabs,
        extraControlsBeforeTabs,
        onTabHeaderClick,
        ribbonTabs,
        selectedTab,
    }: CommandRibbonTopBarProps) {
        const tabListContainerRef = React.useRef<HTMLDivElement>(null);
        const { findAllFocusable, findNextFocusable, findPrevFocusable } = useFocusFinders();
        const isWin32OverlayEnabled = isWin32StylingEnabled();

        // TODO: Remove this workaround when the 1JS fix tracked by work item 453169 is consumed.
        const focusRibbonTab = React.useCallback(
            (tab: HTMLElement) => {
                tab.focus();

                // The v9 TabList does not select tabs when Tab moves focus between them.
                const tabId = tab.getAttribute('value') ?? tab.dataset.uniqueId;
                if (tabId && tabId !== selectedTab.id) {
                    onTabHeaderClick(tabId);
                }
            },
            [onTabHeaderClick, selectedTab.id]
        );

        const onTabKeyDownCapture = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key !== 'Tab') {
                    return;
                }

                const tabListContainer = tabListContainerRef.current;
                const topBarElement = event.currentTarget;
                const target = event.target as HTMLElement;
                if (!tabListContainer || !target.closest) {
                    return;
                }

                const focusableElements = findAllFocusable(tabListContainer);
                const tabs = Array.from(
                    tabListContainer.querySelectorAll<HTMLElement>('[role="tab"]')
                ).filter(
                    element =>
                        !element.hasAttribute('disabled') &&
                        element.getAttribute('aria-disabled') !== 'true'
                );
                const currentTab = target.closest<HTMLElement>('[role="tab"]');
                const overflowButton =
                    tabListContainer.querySelector<HTMLElement>('#pivotOverflowButton');

                let nextElement: HTMLElement | null | undefined;
                let shouldSelectTab = false;

                // Own Tab navigation so focus and selection remain synchronized.
                if (currentTab && tabListContainer.contains(currentTab)) {
                    const currentIndex = tabs.indexOf(currentTab);
                    if (currentIndex < 0) {
                        return;
                    }

                    nextElement = event.shiftKey ? tabs[currentIndex - 1] : tabs[currentIndex + 1];
                    shouldSelectTab = !!nextElement;

                    if (
                        !nextElement &&
                        !event.shiftKey &&
                        overflowButton &&
                        focusableElements.includes(overflowButton)
                    ) {
                        nextElement = overflowButton;
                    }
                } else if (overflowButton?.contains(target)) {
                    if (event.shiftKey) {
                        nextElement = tabs[tabs.length - 1];
                        shouldSelectTab = !!nextElement;
                    }
                } else {
                    const findFocusable = event.shiftKey ? findPrevFocusable : findNextFocusable;
                    const adjacentElement = findFocusable(target, { container: topBarElement });

                    if (!adjacentElement || !tabListContainer.contains(adjacentElement)) {
                        return;
                    }

                    // Bypass the v9 TabList's focusable toolbar wrapper when entering the tabs.
                    nextElement = event.shiftKey
                        ? overflowButton && focusableElements.includes(overflowButton)
                            ? overflowButton
                            : tabs[tabs.length - 1]
                        : (tabs.find(tab => tab.getAttribute('aria-selected') === 'true') ?? // This is a temporary solution. Once the 1JS bug is fixed. We should revert this change. TODO: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/470667
                          tabs[0]);
                    shouldSelectTab = nextElement?.getAttribute('role') === 'tab';
                }

                if (!nextElement) {
                    // Skip the TabList's focus wrapper when leaving for adjacent ribbon controls.
                    const findFocusable = event.shiftKey ? findPrevFocusable : findNextFocusable;
                    const focusRoot = tabListContainer.ownerDocument.body;
                    let currentElement = currentTab ?? overflowButton ?? tabListContainer;

                    do {
                        nextElement = findFocusable(currentElement, { container: focusRoot });
                        if (nextElement === currentElement) {
                            nextElement = undefined;
                            break;
                        }
                        currentElement = nextElement ?? currentElement;
                    } while (nextElement && tabListContainer.contains(nextElement));
                }

                if (nextElement) {
                    event.preventDefault();
                    event.stopPropagation();

                    if (shouldSelectTab) {
                        focusRibbonTab(nextElement);
                    } else {
                        nextElement.focus();
                    }
                }
            },
            [findAllFocusable, findNextFocusable, findPrevFocusable, focusRibbonTab]
        );

        return (
            <div
                id="TopBar"
                className={classnames(topBar, {
                    [topBarWin32Overlay]: isWin32OverlayEnabled,
                })}
                onKeyDownCapture={onTabKeyDownCapture}
            >
                {extraControlsBeforeTabs}
                <div ref={tabListContainerRef} className={topBarContainer} onKeyDown={onKeyDown}>
                    <RibbonTopBar
                        appColors={getAppColors()}
                        appStrings={getAppStrings()}
                        customStyles={
                            isWin32OverlayEnabled
                                ? win32OverlayCustomTopBarStyles
                                : customTopBarStyles
                        }
                        displayDynamicTabHeaderIndicator={true}
                        isTabIndicatorRounded={true}
                        onTabHeaderClick={onTabHeaderClick}
                        overflowTabHeaderMenuLauncherIcon={appIcons.tabListOverflowLauncher}
                        ribbonTabs={customizeTabStyles(ribbonTabs)}
                        selectedTabId={selectedTab.id}
                    />
                </div>
                {extraControlsAfterTabs}
            </div>
        );
    },
    'CommandRibbonTopBar'
);

/** Removing the default background color to support the bleed-through themes */
const customTopBarStyles: RibbonTopBarProps['customStyles'] = {
    root: {
        background: 'none',
    },
};

const win32OverlayCustomTopBarStyles: RibbonTopBarProps['customStyles'] = {
    root: {
        backgroundColor: 'transparent',
    },
};

/** Removing the default background color from each tab to support the bleed-through themes */
const customizeTabStyles = (tabs: RibbonTabDefinitionBase[]): RibbonTabDefinitionBase[] => {
    const styles: TabHeader['styles'] = isWin32StylingEnabled()
        ? {
              wrapper: {
                  margin: 0,
              },
              ghostPivotLabel: {
                  paddingLeft: RIBBON_TAB_HORIZONTAL_PADDING,
                  paddingRight: RIBBON_TAB_HORIZONTAL_PADDING,
              },
              pivotHeader: {
                  root: {
                      backgroundColor: 'transparent',
                      fontSize: RIBBON_TAB_FONT_SIZE,
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                      lineHeight: RIBBON_TAB_LINE_HEIGHT,
                      minHeight: RIBBON_TAB_MIN_HEIGHT,
                      padding: `0 ${RIBBON_TAB_HORIZONTAL_PADDING}`,
                  },
                  rootHovered: {
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                  },
                  rootPressed: {
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                  },
                  rootFocused: {
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                  },
              },
              selectedPivotHeader: {
                  root: {
                      backgroundColor: 'transparent',
                  },
              },
          }
        : {
              pivotHeader: {
                  root: {
                      background: 'none',
                  },
              },
          };

    return tabs.map(tab => ({ ...tab, styles }));
};

const onKeyDown: React.KeyboardEventHandler = ({ key }) => {
    if (key === 'ArrowDown') {
        const firstBottomBarButton = document
            .getElementById('BottomBar')
            ?.querySelectorAll('button')[0];

        firstBottomBarButton?.focus();
    }
};
